// SCRUM-1627 (epic SCRUM-1609/SCRUM-1624) — global in-app toast host.
//
// The app has a global NotificationService (a BehaviorSubject store) but no
// component was rendering it, so success/error/info calls were invisible.
// This host subscribes once, at the app-root level, so toasts appear from any
// page and survive in-app navigation (e.g. a Research/Analysis generation that
// finishes after the user has moved on). Auto-dismiss is handled by the
// service's per-notification duration.

import { ChangeDetectionStrategy, Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { Notification, NotificationService } from '../services/notification.service';

@Component({
  selector: 'app-notification-host',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="toast-host" data-testid="app-toast-host" aria-live="polite">
      <div
        *ngFor="let n of (notifications$ | async); trackBy: trackById"
        class="toast toast--{{ n.type }}"
        [class.toast--clickable]="!!n.action"
        data-testid="app-toast"
        [attr.data-toast-type]="n.type"
        [attr.role]="n.action ? 'button' : 'status'"
        [attr.tabindex]="n.action ? 0 : null"
        (click)="activate(n)"
        (keydown.enter)="activate(n)"
        (touchstart)="onTouchStart($event)"
        (touchend)="onTouchEnd($event, n.id)"
      >
        <i class="pi" [ngClass]="iconFor(n.type)"></i>
        <span class="toast__msg">{{ n.message }}</span>
        <i *ngIf="n.action" class="pi pi-angle-right toast__go" aria-hidden="true"></i>
        <button
          type="button"
          class="toast__close"
          aria-label="Dismiss"
          data-testid="app-toast-close"
          (click)="$event.stopPropagation(); dismiss(n.id)"
        >&times;</button>
      </div>
    </div>
  `,
  styles: [`
    .toast-host {
      position: fixed;
      top: 68px;
      right: 16px;
      z-index: 12000;
      display: flex;
      flex-direction: column;
      gap: 8px;
      max-width: 360px;
    }
    .toast {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px 14px;
      border-radius: 8px;
      color: #fff;
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
      font-size: 14px;
      animation: toast-in 0.18s ease-out;
    }
    .toast__msg { flex: 1; }
    .toast__close {
      background: transparent;
      border: none;
      color: inherit;
      font-size: 18px;
      line-height: 1;
      cursor: pointer;
      opacity: 0.85;
    }
    .toast__close:hover { opacity: 1; }
    .toast--clickable { cursor: pointer; }
    .toast__go { opacity: 0.9; font-size: 15px; }
    .toast--success { background: #16a34a; }
    .toast--error   { background: #dc2626; }
    .toast--warn    { background: #d97706; }
    .toast--info    { background: #2563eb; }
    @keyframes toast-in {
      from { transform: translateX(12px); opacity: 0; }
      to   { transform: translateX(0);    opacity: 1; }
    }
  `],
})
export class NotificationHostComponent {
  readonly notifications$: Observable<Notification[]> = this.notify.getNotifications();

  // SCRUM-1943 — swipe-up-to-dismiss: remember where a touch began so touchend
  // can tell an upward swipe from a tap.
  private touchStartY: number | null = null;
  private static readonly SWIPE_DISMISS_PX = 40;

  constructor(
    private readonly notify: NotificationService,
    private readonly router: Router,
  ) {}

  dismiss(id: string): void {
    this.notify.remove(id);
  }

  /** SCRUM-1943 — tapping an actionable toast navigates to its target and dismisses it. */
  activate(n: Notification): void {
    if (!n.action) return;
    void this.router.navigate(n.action.route);
    this.notify.remove(n.id);
  }

  onTouchStart(ev: TouchEvent): void {
    this.touchStartY = ev.changedTouches[0]?.clientY ?? null;
  }

  /** Dismiss on an upward swipe (no navigation); a plain tap falls through to activate(). */
  onTouchEnd(ev: TouchEvent, id: string): void {
    if (this.touchStartY === null) return;
    const endY = ev.changedTouches[0]?.clientY ?? this.touchStartY;
    const swipedUp = this.touchStartY - endY > NotificationHostComponent.SWIPE_DISMISS_PX;
    this.touchStartY = null;
    if (swipedUp) {
      ev.preventDefault(); // stop the trailing click so we don't also navigate
      this.dismiss(id);
    }
  }

  trackById(_i: number, n: Notification): string {
    return n.id;
  }

  iconFor(type: Notification['type']): string {
    switch (type) {
      case 'success': return 'pi-check-circle';
      case 'error': return 'pi-times-circle';
      case 'warn': return 'pi-exclamation-triangle';
      default: return 'pi-info-circle';
    }
  }
}
